// ── 주별 달력 뷰 ──────────────────────────────────────────────────────────────
import React, { useMemo } from 'react';
import {
  STATUS_META,
  WEEKDAY_NAMES,
  WEEKDAY_ORDER,
  getTodayYmd,
  addDays,
  timeToPercent,
} from './utils';

/**
 * 주별 달력 그리드 컴포넌트
 * Props:
 *   calData            — API 응답 달력 데이터
 *   weekStart          — 주 시작일 (일요일, YYYY-MM-DD)
 *   filteredEmployees  — 표시할 직원 목록 (필터 적용 후)
 *   onCellClick(cell, ymd, day) — 셀 클릭 콜백
 */
export function WeekView({ calData, weekStart, filteredEmployees, onCellClick }) {
  const todayYmd = getTodayYmd();

  // 이번 주 7일 날짜 배열 (일~토)
  const weekDates = useMemo(() => {
    return WEEKDAY_ORDER.map((_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // date → day 객체 맵
  const dayMap = useMemo(() => {
    const map = {};
    (calData?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [calData]);

  // 필터된 직원 중 실제 데이터가 있는 날짜 집합
  // 휴무(offday)·공휴일(holiday)은 데이터 없음으로 간주 — 해당 날은 좁은 컬럼
  const NO_DATA_STATUSES = new Set(['offday', 'holiday']);
  const hasDataSet = useMemo(() => {
    const set = new Set();
    weekDates.forEach((ymd) => {
      const day = dayMap[ymd];
      if (!day) return;
      const hasAny = filteredEmployees.some((emp) =>
        day.cells?.some((c) => c.user_id === emp.id && !NO_DATA_STATUSES.has(c.status))
      );
      if (hasAny) set.add(ymd);
    });
    return set;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDates, dayMap, filteredEmployees]);

  // 데이터 있는 날: 넓은 고정 너비 / 없는 날: 좁은 너비
  const COL_W_DATA  = 140; // px — 데이터 있는 날
  const COL_W_EMPTY =  44; // px — 데이터 없는 날 (휴무·공휴일 등)

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700, tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {/* 직원 열 헤더 */}
            <th style={{
              width: 80,
              padding: '8px 6px',
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border)',
              fontSize: 13,
              textAlign: 'left',
            }}>직원</th>

            {/* 날짜별 헤더 */}
            {weekDates.map((ymd, i) => {
              const wd = WEEKDAY_ORDER[i];
              const day = dayMap[ymd];
              const isHoliday = day?.is_holiday;
              const isToday = ymd === todayYmd;
              return (
                <th key={ymd} style={{
                  width: hasDataSet.has(ymd) ? COL_W_DATA : COL_W_EMPTY,
                  padding: '8px 6px',
                  textAlign: 'center',
                  background: isToday ? '#eff6ff' : 'var(--bg-subtle)',
                  border: `1px solid ${isToday ? '#93c5fd' : 'var(--border)'}`,
                  fontSize: 12,
                  color: isHoliday || wd === 'sun' ? '#dc2626'
                       : wd === 'sat' ? '#2563eb'
                       : 'var(--text-sub)',
                }}>
                  {/* 요일 이름 */}
                  <div style={{ fontWeight: 700 }}>{WEEKDAY_NAMES[wd]}</div>
                  {/* 날짜 (MM-DD 형식) */}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ymd.slice(5)}</div>
                  {/* 공휴일명 표시 */}
                  {isHoliday && <div style={{ fontSize: 9, color: '#dc2626', fontWeight: 600 }}>{day?.holiday_name || '공휴일'}</div>}
                  {/* 일별 출근·결근 요약 */}
                  {day && (
                    <div style={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                      {day.summary.present > 0 && (
                        <span style={{ fontSize: 9, background: '#dcfce7', color: '#166534', borderRadius: 2, padding: '0 3px' }}>
                          출{day.summary.present}
                        </span>
                      )}
                      {day.summary.absent > 0 && (
                        <span style={{ fontSize: 9, background: '#fee2e2', color: '#991b1b', borderRadius: 2, padding: '0 3px' }}>
                          결{day.summary.absent}
                        </span>
                      )}
                      {day.leave_overlap_warning && <span style={{ fontSize: 9 }}>⚠️</span>}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {filteredEmployees.map((emp) => (
            <tr key={emp.id}>
              {/* 직원 이름·부서 열 */}
              <td style={{ padding: '6px 6px', border: '1px solid var(--border)', fontSize: 12 }}>
                <div style={{ fontWeight: 600 }}>{emp.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{emp.department || '—'}</div>
              </td>

              {/* 날짜별 셀 */}
              {weekDates.map((ymd) => {
                const day = dayMap[ymd];
                const cell = day?.cells?.find((c) => c.user_id === emp.id);

                // 휴무(offday)·공휴일(holiday) 또는 데이터 없음 → 빈 칸
                if (!cell || cell.status === 'offday' || cell.status === 'holiday') {
                  return (
                    <td key={ymd} style={{ padding: 4, border: '1px solid var(--border)', background: 'var(--bg-subtle)' }} />
                  );
                }

                const meta = STATUS_META[cell.status] || STATUS_META.scheduled;

                // 타임라인 배경 바: 미래 날짜는 예정 시간, 과거·오늘은 실제 출퇴근
                const wBarStart = ymd > todayYmd ? cell.schedule_start : cell.check_in;
                const wBarEnd   = ymd > todayYmd ? cell.schedule_end   : cell.check_out;
                const wBarSPct  = timeToPercent(wBarStart);
                const wBarEPct  = timeToPercent(wBarEnd);
                const wShowBar  = wBarSPct !== null && wBarEPct !== null && wBarEPct > wBarSPct;

                return (
                  <td
                    key={ymd}
                    onClick={() => onCellClick(cell, ymd, day)}
                    style={{
                      position: 'relative', // 타임라인 바 절대 위치용
                      padding: '5px 6px',
                      border: `1px solid ${meta.border}`,
                      background: meta.bg,
                      cursor: 'pointer',
                      verticalAlign: 'top',
                    }}
                  >
                    {/* 타임라인 배경 바 (td 전체 폭 기준, 시간 비율로 너비 결정) */}
                    {wShowBar && (
                      <div style={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: `${wBarSPct}%`,
                        width: `${wBarEPct - wBarSPct}%`,
                        background: meta.text, opacity: 0.10,
                        pointerEvents: 'none', zIndex: 0,
                      }} />
                    )}

                    {/* 상태 레이블 */}
                    <div style={{
                      position: 'relative', zIndex: 1,
                      fontSize: 11, fontWeight: 600, color: meta.text, marginBottom: 2,
                    }}>
                      {STATUS_META[cell.status]?.label || '—'}
                    </div>

                    {/* 시간·근무지 상세 (z-index로 타임라인 바 위에 표시) */}
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      {ymd > todayYmd ? (
                        /* 미래 날짜: 예정 출퇴근 시간 */
                        <>
                          {cell.schedule_start && (
                            <div style={{ fontSize: 10, color: 'var(--text-sub)' }}>
                              {cell.schedule_start}{cell.schedule_end ? ` - ${cell.schedule_end}` : ''}
                            </div>
                          )}
                          {/* 미래 날짜는 근무지를 표시하지 않음 (실제 출근 기록 있는 날에만 표시) */}
                        </>
                      ) : (
                        /* 과거·오늘: 실제 출퇴근 기록 */
                        <>
                          {cell.check_in && (
                            <div style={{
                              fontSize: 10, color: 'var(--text-sub)',
                              display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
                            }}>
                              <span>{cell.check_in} → {cell.check_out || '?'}</span>
                              {cell.late_minutes > 0 && (
                                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                  ({cell.late_minutes}분 지각)
                                </span>
                              )}
                            </div>
                          )}
                          {/* 예외 스케줄 적용 여부 표시 */}
                          {cell.has_override && (
                            <div style={{ fontSize: 10, color: '#92400e' }}>예외적용</div>
                          )}
                          {/* 근무지 표시 */}
                          {(cell.registered_ip_label || cell.check_in_address) && (
                            <div style={{
                              fontSize: 10, color: 'var(--text-muted)',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              📍{cell.registered_ip_label || cell.check_in_address}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
